import React from 'react';
import { object, string, func } from 'prop-types';
import _trimStart from 'lodash/trimStart';
import querystring from 'querystring';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isLoading } from '../behaviors/loading';
import { isError } from '../behaviors/error';
import { configSelector } from '../behaviors/configuration';
import {
  fetchLocationV8,
  fetchLocation,
  getLocationById,
  getLocationProviders,
  fetchProvidersByLocation,
  receiveLocationError
} from '../behaviors/locations';
import { receiveProfileError } from '../behaviors/profiles';
import { productNameSelector } from '../behaviors/product-name';
import { pages } from '../tracking/constants';

import LocationSummaryV8 from '../location-summary/index';
import { LocationV9 } from '../location-v9';
import { decorateLocationTracking } from './logging-decorators';
import { pageViewEvent } from '../tracking/tracking-utils';
import { getCustomerCode } from '../behaviors/customer-code/selectors';
import { searchSummarySelector } from '../behaviors/search-v9/selectors';
import { settingsSelector } from '../behaviors/settings';
import {
  fetchProviderAvailability,
  fetchPurposesByProviderId,
  fetchSlots
} from '../behaviors/availability';

import { getLocationPath } from 'Common/urls/location';
import { isDarkshipUseListPageSearchV9 } from 'Common/config';

const mapStateToProps = (state, ownProps) => {
  // kyruus apis expect locationId to be uppercase
  const locationId = (ownProps.match.params.id || '').toUpperCase();
  // location prop is already being passed in, so calling the location
  // object that comes back from location service locationObject
  const locationObject = getLocationById(state, locationId);
  const providers = getLocationProviders(state, locationId);

  const config = configSelector(state);
  const loading = isLoading(state);
  const error = isError(state);
  const currentQuery = querystring.parse(
    _trimStart(ownProps.location.search, '?')
  );

  const log = (message, event_data = {}) => {
    const additionalTrackingData = decorateLocationTracking({
      state,
      routeMatch: ownProps.match
    });

    ownProps.log(message, { ...event_data, ...additionalTrackingData });
  };

  const customerCode = getCustomerCode(state);

  const searchSummary = searchSummarySelector(state);

  const productName = productNameSelector(state);

  const appSettings = settingsSelector(state);

  // Add availability-related state for location page
  const appointmentInfo = state.availability
    ? {
        loading: state.availability.loading,
        purposeOptions: state.availability.purposeOptions,
        ...(state.availability.patientRel && {
          relationship: state.availability.patientRel
        }),
        ...(state.availability.purpose && {
          purpose: state.availability.purpose
        })
      }
    : undefined;

  return {
    config,
    globalLoading: loading,
    globalError: error,
    currentQuery,
    locationId,
    locationObject,
    providers,
    log,
    customerCode,
    productName,
    searchSummary,
    appSettings,
    appointmentInfo,
    slots: state.availability?.slotsByProviderId,
    slotsStatus: state.availability?.slotsStatusByProviderId,
    purposeOptionsByProviderId: state.availability?.purposeOptionsByProviderId,
    availabilityError: state.availability?.error,
    purposesLoading: state.availability?.purposesLoading
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchLocationV8,
      fetchLocation,
      fetchProvidersByLocation,
      receiveLocationError,
      receiveProfileError,
      fetchProviderAvailability,
      fetchPurposesByProviderId,
      fetchSlots
    },
    dispatch
  );
};

class LocationContainer extends React.Component {
  async serverSideExecute({ req, res, store }) {
    const {
      currentQuery,
      fetchLocation,
      fetchLocationV8,
      fetchProvidersByLocation,
      receiveProfileError,
      location,
      locationId,
      searchSDK
    } = this.props;
    let canonicalLocationId = locationId;

    const v9Search = isDarkshipUseListPageSearchV9(this.props.config);
    const locationReq = v9Search
      ? fetchLocation({ locationId, searchSDK, req })
      : fetchLocationV8(locationId, req);
    const providersReq = fetchProvidersByLocation(
      locationId,
      { ...currentQuery, page: 1 },
      req
    );
    const [locationRes, providersRes] = await Promise.allSettled([
      locationReq,
      providersReq
    ]);
    if (locationRes.status === 'rejected') {
      if (
        locationRes.reason?.status === 404 ||
        locationRes.reason?.status === 400
      ) {
        store.dispatch(receiveProfileError(locationRes.reason));
        res.status(404);
        return;
      }
      req
        .getLog()
        .error(
          `Error fetching location ${locationId}: ${locationRes.reason?.message}`
        );
    }
    if (providersRes.status === 'rejected') {
      req
        .getLog()
        .error(
          `Error fetching location providers ${locationId}: ${providersRes.reason?.message}`
        );
    }

    // Get canonical ID from API response for consistent retrieval
    if (locationRes?.value?._result?.id) {
      canonicalLocationId = locationRes.value._result.id;
    }

    const state = store.getState();
    const locationObject = getLocationById(state, canonicalLocationId);

    processLocationResponse({ location, locationObject, res });
  }

  fetchSlotsForCurrentProviders() {
    const { providers, fetchSlots } = this.props;

    if (!fetchSlots || !providers?.providers?.length) {
      return;
    }
    const providerIds = providers.providers.map((p) => p.id);
    const apptInfo = {
      relationship: null,
      purpose: null,
      visitMethod: null
    };
    fetchSlots(apptInfo, providerIds);
  }

  componentDidMount() {
    const { log } = this.props;

    window.scrollTo(0, 0);

    log(pageViewEvent(pages.LOCATION));

    this.fetchSlotsForCurrentProviders();
  }

  componentDidUpdate(previousProps) {
    const { location, providers } = this.props;

    if (location.pathname !== previousProps.location.pathname) {
      window.scrollTo(0, 0);
      this.fetchSlotsForCurrentProviders();
    } else if (
      providers?.providers?.length !==
      previousProps.providers?.providers?.length
    ) {
      this.fetchSlotsForCurrentProviders();
    }
  }

  render() {
    const v9Search = isDarkshipUseListPageSearchV9(this.props.config);
    if (v9Search) {
      return <LocationV9 {...this.props} />;
    }
    return <LocationSummaryV8 {...this.props} />;
  }
}

export function processLocationResponse({ location, locationObject, res }) {
  if (!locationObject) {
    res.status(404);
  } else {
    const expectedCanonical = getLocationPath(
      locationObject.name,
      locationObject.id
    );

    if (location.pathname !== expectedCanonical) {
      const redirectUrl = `${expectedCanonical}${location.search || ''}`;
      res.redirect(301, redirectUrl);
    }
  }
}

LocationContainer.propTypes = {
  currentQuery: object.isRequired,
  config: object.isRequired,
  locationId: string.isRequired,
  locationObject: object,
  providers: object,
  customerCode: string.isRequired,
  searchSummary: object.isRequired,
  fetchLocation: func.isRequired,
  fetchLocationV8: func.isRequired,
  fetchProvidersByLocation: func.isRequired,
  fetchProviderAvailability: func.isRequired,
  fetchPurposesByProviderId: func.isRequired,
  fetchSlots: func.isRequired,
  log: func.isRequired
};

export { LocationContainer, mapStateToProps };
export default connect(mapStateToProps, mapDispatchToProps)(LocationContainer);
